import React, {useState} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import DeleteDialog from '@pluto/delete-dialog';
import {OnStringSetState} from '@pluto/@types';
import {TimespanCategory} from '@pluto/rest';
import {ToggleSelectedMarker} from 'VideoAudioQC/Timeline/Redux/TimelineActions';
import {
    MarkupMarker,
    TimelineRowType,
} from 'VideoAudioQC/Timeline/TimelineTypes';
import {
    RemoveMarkupMarkerAction,
    UpdateMarkupMarkerAction,
} from 'VideoAudioQC/Timeline/Markup/Redux/MarkupActions';
import {Item} from 'VideoAudioQC/QCMedia/Redux/QCMediaActions';
import {onQcNoteZoomIn} from 'VideoAudioQC/Timeline/TimelineUtils';
import {OnEditMarkupMarkerProps} from './Table/TableContainer';
import {MarkupData} from './Table/Table';
import Markup from './Markup';

import './MarkupContainer.scss';

export interface MarkupContainerProps {
    item: Item;
    identifier: string;
    markers: MarkupMarker[];
    selectedRowId: string;
    selectedMarkerId: string;
    noteTypes: TimespanCategory[];
    onCreateMarker: (rowId: string) => void;
    toggleSelectedMarker: ToggleSelectedMarker;
    removeMarkupMarker: RemoveMarkupMarkerAction;
    updateMarkupMarker: UpdateMarkupMarkerAction;
    jobDeliveryDate: string;
}

export const onMarkerClick = curry((props: MarkupContainerProps, onQcNoteZoomIn: (marker: MarkupData) => void, marker: MarkupData) => {
    const {
        identifier,
        toggleSelectedMarker,
    } = props;
    
    toggleSelectedMarker(marker.identifier, identifier, TimelineRowType.Markup);
    onQcNoteZoomIn(marker);
});

export const onCreateMarkerClick = wrap((props: MarkupContainerProps) => {
    const {
        identifier,
        onCreateMarker,
    } = props;
    
    onCreateMarker(identifier);
});

export const onShowDeleteDialog = curry((setDeleteId: OnStringSetState, item: MarkupData) => {
    const {identifier} = item;
    
    setDeleteId(identifier);
});

export type DeleteDialogProps = {
    deleteId: string,
    url: string,
    setDeleteId: OnStringSetState,
    removeMarkupMarker: RemoveMarkupMarkerAction,
};

export const onCloseDialog = curry((props: DeleteDialogProps, confirm: boolean) => {
    const {
        url,
        deleteId,
        setDeleteId,
        removeMarkupMarker,
    } = props;
    
    if (confirm) {
        removeMarkupMarker(url, deleteId);
    }
    
    setDeleteId('');
});

export const titleText = 'Are you sure you want to delete the markup?';

export const getDeleteDialog = (props: DeleteDialogProps) => {
    const {deleteId} = props;
    const handleClose = onCloseDialog(props);
    
    if (!deleteId || deleteId.length === 0)
        return null;
    
    return (
        <DeleteDialog
            text={titleText}
            onCloseDialog={handleClose}
        />
    );
};

export const onEditMarker = curry((props: MarkupContainerProps, markerProps: OnEditMarkupMarkerProps) => {
    const {
        updateMarkupMarker,
        item,
    } = props;
    const {
        marker,
        inpoint,
    } = markerProps;
    const {markups} = item;
    const {duration} = item.video;
    
    updateMarkupMarker({
        url: markups,
        duration,
        marker,
        inpoint,
    });
});

export const onClickOutsideList = wrap((props: MarkupContainerProps) => {
    const {
        identifier,
        toggleSelectedMarker,
        selectedMarkerId,
    } = props;
    
    if (!selectedMarkerId)
        return;
    
    // Deselect selected marker
    toggleSelectedMarker(selectedMarkerId, identifier, TimelineRowType.Markup);
});

export default function MarkupContainer(props: MarkupContainerProps) {
    const {
        markers,
        selectedMarkerId,
        item,
        noteTypes,
        removeMarkupMarker,
        jobDeliveryDate,
    } = props;
    const {markups} = item;
    
    const [deleteId, setDeleteId] = useState<string>('');
    const handleMarkerClick = onMarkerClick(props, onQcNoteZoomIn);
    const handleCreateMarkerClick = onCreateMarkerClick(props);
    const handleShowDeleteDialog = onShowDeleteDialog(setDeleteId);
    const handleEditMarker = onEditMarker(props);
    
    const deleteDialog = getDeleteDialog({
        url: markups,
        deleteId,
        setDeleteId,
        removeMarkupMarker,
    });
    
    const handleOnClickOutsideList = onClickOutsideList(props);
    
    return (
        <div className="markup-table-container">
            <Markup
                jobDeliveryDate={jobDeliveryDate}
                markers={markers}
                selectedMarkerId={selectedMarkerId}
                item={item}
                noteTypes={noteTypes}
                onCreateMarker={handleCreateMarkerClick}
                onMarkerClick={handleMarkerClick}
                onDeleteClick={handleShowDeleteDialog}
                onEditMarker={handleEditMarker}
                onClickOutsideList={handleOnClickOutsideList}
            />
            {deleteDialog}
        </div>
    );
}
